import { formatInTimeZone } from 'date-fns-tz';
import { guessTimeZone, toCurrentTimeZoneName } from 'utils/dateTime';

export interface TimeZoneOption {
  name: string;
  offset: string;
  reformatName: string;
}

const getTimeZoneNames = (): string[] => {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };
  const names = intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  return Array.from(
    new Set([...names.map(toCurrentTimeZoneName), 'UTC', guessTimeZone()]),
  );
};

const parseArray = (parsedName: string[]) => {
  switch (parsedName.length) {
    case 1:
      return parsedName[0];
    case 2:
      return parsedName[1];
    case 3:
      return parsedName[2] + ', ' + parsedName[1];
    default:
      return parsedName.join('/');
  }
};

const switchOffsetSigns = (offset: string) => {
  switch (offset.charAt(0)) {
    case '-':
      return '+' + offset.substring(1);
    case '+':
      return '-' + offset.substring(1);
  }
};

// API has the sign backwards for timezones of the form GMT+/-4
const fixGMT = (parsedName: string) => {
  if (
    parsedName.length > 3 &&
    parsedName.substring(0, 3) == 'GMT' &&
    '+-'.includes(parsedName.charAt(3))
  ) {
    return 'GMT' + switchOffsetSigns(parsedName.substring(3));
  }
  return parsedName;
};

// CLDR only has an English abbreviation for a zone in the locale of the region
// that uses it (EST in en-US, CET in en-GB, IST in en-IN...), so try those first.
const ABBREVIATION_LOCALES: Record<string, string[]> = {
  Africa: ['en-ZA'],
  America: ['en-US', 'en-CA'],
  Antarctica: ['en-NZ', 'en-AU'],
  Asia: ['en-IN', 'en-SG', 'en-AU'],
  Atlantic: ['en-GB', 'en-CA'],
  Australia: ['en-AU'],
  Europe: ['en-GB', 'en-IE'],
  Indian: ['en-IN', 'en-ZA'],
  Pacific: ['en-NZ', 'en-AU'],
};

const getAbbreviation = (now: Date, timeZone: string): string => {
  const region = timeZone.split('/')[0];
  const locales = [...(ABBREVIATION_LOCALES[region] ?? []), 'en-US', 'en-GB'];
  for (const locale of locales) {
    const name = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(now)
      .find((part) => part.type === 'timeZoneName')?.value;
    // Intl falls back to "GMT+1"-style names when a zone has no abbreviation.
    if (name && !/^([+-]|(GMT|UTC)[+-])/.test(name)) return name;
  }
  return '';
};

const toOption = (timezone: string, now: Date): TimeZoneOption | null => {
  try {
    const parsedName = timezone.split('_').join(' ').split('/');
    const offset = formatInTimeZone(now, timezone, 'xxx');
    const abbrev = getAbbreviation(now, timezone);
    const abbr = abbrev ? abbrev + ' - ' : '';

    return {
      name: timezone,
      offset,
      reformatName:
        '(GMT' + offset + ') ' + abbr + fixGMT(parseArray(parsedName)),
    };
  } catch {
    return null;
  }
};

let cachedOptions: TimeZoneOption[] | null = null;

/** Sorted, de-duplicated IANA time zones for dropdowns. */
export const getTimeZoneOptions = (): TimeZoneOption[] => {
  if (cachedOptions) return cachedOptions;

  const now = new Date();
  cachedOptions = getTimeZoneNames()
    .map((timezone) => toOption(timezone, now))
    .filter((option): option is TimeZoneOption => option !== null)
    .sort((a, b) => {
      if (a.offset === b.offset) {
        return a.reformatName.localeCompare(b.reformatName);
      }
      return parseInt(a.offset) - parseInt(b.offset);
    })
    .filter((element, index, array) => {
      const nextInd = array
        .slice(index + 1)
        .findIndex((e) => e.reformatName === element.reformatName);
      return nextInd == -1;
    });

  return cachedOptions;
};

/** Like getTimeZoneOptions, but guarantees `timeZone` (e.g. a legacy alias) is selectable. */
export const getTimeZoneOptionsIncluding = (
  timeZone?: string,
): TimeZoneOption[] => {
  const options = getTimeZoneOptions();
  if (!timeZone || options.some((o) => o.name === timeZone)) return options;
  const extra = toOption(timeZone, new Date());
  return extra ? [extra, ...options] : options;
};
